import { WinnerCard } from "@/components/WinnerCard"
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import moneyLottie from "@/assets/lottie/money.lottie?url";
import { HeaderTitle } from "@/components/HeaderTitle";
import { Button } from "@/components/ui/button";
import { MediaLogos } from "@/components/MediaLogos";
import { useIsLocationAllowed } from "@/hooks/useIsLocationAllowed";
import { useEffect } from 'react';
import { SafePaddingContainer } from "@/components/SafePaddingContainer";
import { useViewTransition } from "@/hooks/useViewTransition";
import arrowLottie from "@/assets/lottie/arrow.lottie?url";
import { CircularLoadingSpinner } from "@/components/CircularLoadingSpinner/CircularLoadingSpinner";



/**
 * Extracts URL parameters from the current window location
 * @returns Object containing all URL parameters
 */
function getUrlParams(): Record<string, string> {
  const searchParams = new URLSearchParams(window.location.search);
  const params: Record<string, string> = {};
  
  for (const [key, value] of searchParams.entries()) {
    params[key] = value;
  }
  
  return params;
}

/**
 * Saves UTM parameters from URL to localStorage
 * @returns Object containing saved UTM parameters
 */
function saveUtmParamsToLocalStorage(): Record<string, string> {
  const params = getUrlParams();
  
  // Look specifically for UTM parameters
  const utmParams: Record<string, string> = Object.keys(params)
    .filter(key => key.startsWith('utm_'))
    .reduce((obj: Record<string, string>, key) => {
      obj[key] = params[key];
      return obj;
    }, {});
  
  // Save each UTM parameter to localStorage
  for (const [key, value] of Object.entries(utmParams)) {
    localStorage.setItem(key, value);
  }
  
  return utmParams;
}

export function PrizeWinningsPage() {
    const { navigateNormal } = useViewTransition(); 
    const [isLocationAllowed] = useIsLocationAllowed();

    useEffect(() => {
        try {
            saveUtmParamsToLocalStorage();
        } catch (error) {
            console.error('Error saving UTM parameters:', error);
        }
    }, []);

    return (
        <div className="h-screen overflow-y-hidden pb-40 bg-white">
            <SafePaddingContainer orientation="header" className='h-[65%]'>
                <div className="absolute right-4 text-xs opacity-25 z-20 text-right">
                    <div>Yours next? Portrayals of users'</div>
                    <div>experiences of gross winnings</div>
                </div>

                {/* Blue background with clip path */}
                <div 
                    className="absolute inset-0"
                    style={{
                        clipPath: 'polygon(0px 0px, 100% 0px, 100% 49%, 0px 52%)',
                        zIndex: 0,
                        backgroundColor: '#DFF3FF',
                        height: '100vh',
                    }}
                >
                    <DotLottieReact
                        src={moneyLottie}
                        dotLottieRefCallback={(ref) => {
                            ref?.addEventListener("load", () => {
                            ref?.setLayout({ fit: "cover", align: [0.5, 0.5] });
                            });
                        }}
                        renderConfig={{
                            autoResize: true,
                        }}
                        style={{
                            position: "absolute",
                            inset: "0",
                            borderBottomRightRadius: "10vw",
                            borderBottomLeftRadius: "10vw",
                            overflow: "hidden",
                        }}
                        speed={0.5}
                        segment={[65, 130]}
                        autoplay
                        loop
                    />
                </div>

                <div className='h-[5%]'/>
                
                {/* Content container with higher z-index */}
                <div className="relative w-full h-full z-10">
                    <div className="relative w-full">
                        <div className='mt-12 space-y-3 flex flex-col justify-center items-center'>
                            <div className='-translate-x-8'>
                                <WinnerCard name='Aila' rotation={0.5}/>
                            </div>
                            
                            {/* Middle winner card - positioned to overlap others */}
                            <div className='translate-x-8'>
                                <WinnerCard name='Brett' rotation={0.5}/>
                            </div>
                            
                            {/* Bottom winner card */}
                            <div className='-translate-x-2'>
                                <WinnerCard name='Ren' rotation={0.5}/>
                            </div>
                        </div>
                    </div>
                </div>
            </SafePaddingContainer>
            <SafePaddingContainer orientation="footer" className="bg-white">
            <div className='flex justify-center'>    
                    <HeaderTitle size='text-5xl' nameColor="text-blue font-black" showState={false} />
                </div>

                <p className="text-center text-3xl font-semibold text-blue-dark leading-normal my-4 tracking-tight mt-2">
                    Win up to $4.6M on the official SkillScratch app
                </p>

                <Button
                    className="py-8 bg-blue text-white text-2xl font-bold w-full rounded-lg flex items-center justify-center gap-3 mb-2"
                    shine
                    onClick={() => {
                        if (isLocationAllowed?.allowed) {
                            navigateNormal('/onboarding/page0',
                                {
                                  replace: false,
                                  viewTransition: true,
                                });
                        }
                    }}
                    disabled={!isLocationAllowed}
                >
                    <span className='items-center justify-center'>{!isLocationAllowed ? "Loading..." : "Scratch Now"}</span>
                    {
                        !isLocationAllowed ? (
                            <CircularLoadingSpinner />
                        ) : (
                            <span className='h-5 w-5 flex items-center justify-center'>
                                <DotLottieReact
                                    src={arrowLottie}
                                    autoplay
                                    loop
                                    style={{
                                    objectFit: "contain",
                                    }}
                                />
                            </span>
                        )
                    }
                </Button>

                <Button
                    className="py-4 bg-blue-dark text-white text-lg font-semibold w-full rounded-lg flex items-center justify-center gap-3 mb-2"
                    onClick={() => {
                        if (isLocationAllowed?.allowed) {
                            navigateNormal('/login',
                                {
                                  replace: false,
                                  viewTransition: true,
                                });
                        } else {
                            // Should log an error here
                        }
                    }}
                    disabled={!isLocationAllowed}
                >
                    {!isLocationAllowed ? "Loading..." : "I have an account"}
                </Button>
                <div className='mt-auto mb-0'>
                    <MediaLogos />
                </div>
            </SafePaddingContainer>
        </div>
    )
}